import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MessageCircle, Award, Users, Leaf, Heart } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { Process } from '../components/Process';
import { Stats } from '../components/Stats';
import { CLEANING_IMAGES, WHATSAPP_URL } from '../constants/images';
const values = [{
  icon: Heart,
  title: 'Care',
  desc: 'We treat every home like our own — meticulous and respectful.'
}, {
  icon: Leaf,
  title: 'Eco-Conscious',
  desc: 'Safe products for your family, pets, and the planet.'
}, {
  icon: Users,
  title: 'Trusted Team',
  desc: 'Background-checked, vetted, and continuously trained.'
}, {
  icon: Award,
  title: 'Excellence',
  desc: '15+ years of consistently exceeding expectations.'
}];
const checklist = ['Trained & vetted professionals', 'Eco-friendly cleaning products', 'Flexible scheduling', '100% satisfaction guarantee', 'Fully insured and bonded', 'WhatsApp & text-based booking'];
export function AboutPage() {
  return <>
      <PageBanner title="Cleaning, perfected over 15 years." subtitle="A trusted member of MacAnthony Groups, serving homes and businesses across Clifton, NJ." backgroundImage={CLEANING_IMAGES[1]} breadcrumb="About" data-id="element-376" />

      {/* Story */}
      <section className="py-20 md:py-28 bg-white" data-id="element-377">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-378">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-2 gap-4" data-id="element-379">
            <img src={CLEANING_IMAGES[2]} alt="Clean space" className="rounded-3xl aspect-[3/4] object-cover shadow-xl" data-id="element-380" />
            <img src={CLEANING_IMAGES[5]} alt="Clean space" className="rounded-3xl aspect-[3/4] object-cover shadow-xl mt-12" data-id="element-381" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-382">
            <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-383">
              ◆ Our Story
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-brand-ink" data-id="element-384">
              Built on trust. Driven by{' '}
              <span className="italic font-serif text-brand-navy" data-id="element-385">detail.</span>
            </h2>
            <p className="text-lg text-slate-700 mb-6 leading-relaxed" data-id="element-386">
              Global Remote Cleaning Services was founded with a simple belief:
              a clean space transforms how you live and work. For over 15 years,
              we've delivered meticulous, dependable cleaning to families and
              businesses across Clifton, NJ and the surrounding communities.
            </p>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed" data-id="element-387">
              As part of MacAnthony Groups of Companies, we bring institutional
              reliability to a deeply personal service — every clean is
              hand-checked, every client is known by name.
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-388">
              {checklist.map((item, index) => <motion.li key={index} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="flex items-center gap-3" data-id="element-389">
                  <CheckCircle2 className="w-5 h-5 text-brand-green flex-shrink-0" data-id="element-390" />
                  <span className="text-slate-800 font-medium text-sm" data-id="element-391">
                    {item}
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      <Stats data-id="element-392" />

      {/* Values */}
      <section className="py-20 md:py-28 bg-brand-light" data-id="element-393">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-394">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-395">
            <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-396">
              ◆ Our Values
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-ink mb-6" data-id="element-397">
              What guides our{' '}
              <span className="italic font-serif text-brand-navy" data-id="element-398">work.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-399">
            {values.map((value, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            y: -6
          }} className="bg-white rounded-3xl p-8 text-center hover:shadow-soft transition-all" data-id="element-400">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-navy/5 flex items-center justify-center mb-5" data-id="element-401">
                  <value.icon className="w-8 h-8 text-brand-navy" data-id="element-402" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-brand-ink" data-id="element-403">
                  {value.title}
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed" data-id="element-404">
                  {value.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Process data-id="element-405" />

      {/* CTA */}
      <section className="py-24 bg-brand-navy" data-id="element-406">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-407">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-408">
            Ready to experience the{' '}
            <span className="italic font-serif" data-id="element-409">difference?</span>
          </h2>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-green text-white font-bold text-lg hover:bg-brand-greenDark transition-all hover:-translate-y-1" data-id="element-410">
            <MessageCircle className="w-5 h-5" data-id="element-411" />
            Message us on WhatsApp
          </a>
        </div>
      </section>
    </>;
}